/**
 * Engine: workflow matching.
 *
 * Pure duck-typed selectors over the form's applicable workflows. Given the
 * node's entityOrGroupId they pick out the workflows the React layer should
 * invoke on mark-winning / unmark-winning, and given the full workflow list
 * plus the current node they pick out send-workflows for the menu (single =
 * per-node row, multi = root-level "send to all"). Send matches are scoped to
 * workflows whose target source is the relationship tree itself
 * (`targetSource.sourceType === 'relationshipTree'`) and whose
 * `entityOrGroupIds` scope (when present) contains the node's entity. This
 * keeps tableColumn-sourced or cross-entity send workflows out of the wrong
 * row's menu. Returning structured matches (not booleans) keeps the call
 * sites free of action-shape inspection.
 */
import type {
  Workflow,
  WorkflowSendAction,
  WorkflowSetEntityReferenceAction,
  WorkflowUserParameter,
} from '../System.Workflow.types';

export type MarkWinningWorkflowMatch = {
  workflow: Workflow;
  parameterId: string;
};

export type SendWorkflowMatch = {
  workflow: Workflow;
  mode: 'single' | 'multi';
};

export const findMarkWinningWorkflows = (
  workflows: Workflow[],
  entityOrGroupId: string,
): MarkWinningWorkflowMatch[] => {
  const matches: MarkWinningWorkflowMatch[] = [];
  for (const workflow of workflows) {
    const action = (workflow.actions ?? []).find(
      (a): a is WorkflowSetEntityReferenceAction =>
        a.actionType === 'setEntityReference' &&
        a.entityOrGroupId === entityOrGroupId &&
        a.mode === 'set',
    );
    if (!action) continue;
    const recordIdSource = action.entityRecordId;
    if (
      !recordIdSource ||
      typeof recordIdSource !== 'object' ||
      recordIdSource.sourceType !== 'userParameter'
    ) {
      continue;
    }
    matches.push({ workflow, parameterId: recordIdSource.parameterId });
  }
  return matches;
};

export const findUnmarkWinningWorkflows = (
  workflows: Workflow[],
  entityOrGroupId: string,
): Workflow[] =>
  workflows.filter((workflow) =>
    (workflow.actions ?? []).some(
      (a): a is WorkflowSetEntityReferenceAction =>
        a.actionType === 'setEntityReference' &&
        a.entityOrGroupId === entityOrGroupId &&
        a.mode === 'clear',
    ),
  );

/**
 * Returns send-workflow matches for a tree node. A workflow matches when:
 *  - It declares a `send` action with a `targetsParameterId`.
 *  - That parameter is a `resolvedSelect` whose `targetSource.sourceType` is
 *    `'relationshipTree'` (so tableColumn-sourced send workflows are
 *    excluded).
 *  - If the parameter's `targetSource.entityOrGroupIds` is set, the node's
 *    `entityOrGroupId` must be in that list.
 *
 * A workflow with multiple matching `send` actions emits one match per action;
 * callers render one menu item per match.
 */
export const findSendWorkflowsForNode = (
  workflows: Workflow[],
  nodeEntityOrGroupId: string,
): SendWorkflowMatch[] => {
  const matches: SendWorkflowMatch[] = [];
  for (const workflow of workflows) {
    const sendActions = (workflow.actions ?? []).filter(
      (a): a is WorkflowSendAction =>
        a.actionType === 'send' && typeof a.targetsParameterId === 'string',
    );
    for (const sendAction of sendActions) {
      const targetsParameterId = sendAction.targetsParameterId;
      const resolvedParam = (workflow.userParameters ?? []).find(
        (p: WorkflowUserParameter): p is Extract<WorkflowUserParameter, { type: 'resolvedSelect' }> =>
          p.type === 'resolvedSelect' && p.parameterId === targetsParameterId,
      );
      if (!resolvedParam) continue;
      if (resolvedParam.targetSource.sourceType !== 'relationshipTree') continue;
      const allowedEntities = resolvedParam.targetSource.entityOrGroupIds;
      if (
        allowedEntities &&
        allowedEntities.length > 0 &&
        !allowedEntities.includes(nodeEntityOrGroupId)
      ) {
        continue;
      }
      matches.push({ workflow, mode: resolvedParam.mode });
    }
  }
  return matches;
};
